/**
 * Auth store — manages authentication state.
 */
import { create } from "zustand";
import type { AuthUser, OAuthProviderInfo } from "./api";
import { authApi } from "./api";

export interface AuthState {
  /** Current authenticated user (null = not logged in) */
  user: AuthUser | null;
  /** Whether the auth state is still being loaded */
  isLoading: boolean;
  /** Whether initialize() has completed at least once */
  initialized: boolean;
  /** Available OAuth providers */
  providers: OAuthProviderInfo[];

  /** Initialize auth state (fetch user + providers). Idempotent — one in-flight request. */
  initialize: () => Promise<void>;
  /** Clear user state (after logout) */
  clearUser: () => void;
}

let initPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  initialized: false,
  providers: [],

  initialize: () => {
    if (get().initialized) return Promise.resolve();
    if (initPromise) return initPromise;

    initPromise = (async () => {
      set({ isLoading: true });
      try {
        const [user, providers] = await Promise.all([
          authApi.fetchMe(),
          authApi.fetchProviders(),
        ]);
        set({ user, providers, isLoading: false, initialized: true });
      } catch {
        set({ user: null, providers: [], isLoading: false, initialized: true });
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  },

  clearUser: () => set({ user: null }),
}));

/** Reset init dedupe state — for tests only. */
export function resetAuthStoreForTests(): void {
  initPromise = null;
  useAuthStore.setState({
    user: null,
    isLoading: true,
    initialized: false,
    providers: [],
  });
}
